/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */

import { AnalyticsContext, AnalyticsProduct } from 'utils/analytics/events';
import {
  CartContext,
  ContextCache,
  DispensaryContext,
  EcommGTMContext,
  GTMCartItem,
  UserContext,
} from '../internal-gtm-tracker.types';

export type StoreNameDispensary = {
  name?: string | null;
  googleTagManager?: {
    flags?: {
      store_name_override?: string | null;
    } | null;
  } | null;
};

type EcommerceGTMMetaData = {
  context: AnalyticsContext;
  contextCache: ContextCache;
  payload?: {
    cartId: string;
    products: AnalyticsProduct[];
    dispensaryId: string;
    dispensaryName: string;
  };
};

export const ecommerceGTMMetaData = ({ context, contextCache, payload }: EcommerceGTMMetaData): EcommGTMContext => ({
  affiliation:
    getStoreNameFromDispensary(context.activeDispensary) ??
    getStoreNameFromDispensary(contextCache.activeDispensary) ??
    payload?.dispensaryName,
  currency: 'USD',
  value: context.activeCart?.subtotal?.toFixed(2) ?? contextCache.activeCart?.subtotal?.toFixed(2),
});

export const cartItemContext = (
  context: AnalyticsContext,
  contextCache: ContextCache,
  payload?: { cartId: string; products: AnalyticsProduct[]; dispensaryId: string; dispensaryName: string }
): EcommGTMContext => ({
  affiliation:
    getStoreNameFromDispensary(context.activeDispensary) ??
    getStoreNameFromDispensary(contextCache.activeDispensary) ??
    payload?.dispensaryName,
});

export const cartItemToGTMCartItem = (item): GTMCartItem => {
  // Including these as custom parameters per https://developers.google.com/analytics/devguides/collection/ga4/item-scoped-ecommerce
  const provider = item.provider ?? item.product.provider;
  const placement = item.placement ?? null;

  const gtmCartItem: GTMCartItem = {
    item_id: item.product.id,
    item_name: item.product.Name,
    item_category: item.product.type ?? item.product.category,
    item_category_2: item.product.subcategory,
    item_variant: item.option,
    item_additional_variant: item.additionalOption,
    item_brand: item.product.brand?.name ?? item.product.brandName,
    quantity: item.quantity,
    price: item.basePrice ?? item.price ?? item.product?.Prices?.[0],
    item_list_name: item.trackerSource,
  };

  if (provider) {
    gtmCartItem.provider = provider;
  }

  if (placement) {
    gtmCartItem.placement = placement;
  }

  return gtmCartItem;
};

export function getStoreNameFromDispensary(dispensary?: StoreNameDispensary | null): string | null | undefined {
  if (!dispensary) {
    return null;
  }

  return dispensary.googleTagManager?.flags?.store_name_override ?? dispensary.name;
}

export const getDispensaryContext = (context: AnalyticsContext, contextCache: ContextCache): DispensaryContext => {
  const state = context.activeDispensary?.location?.state ?? contextCache.activeDispensary?.location?.state;
  return {
    dispensary_id: context.activeDispensary?.id ?? contextCache.activeDispensary?.id,
    dispensary_name:
      getStoreNameFromDispensary(context.activeDispensary) ??
      getStoreNameFromDispensary(contextCache.activeDispensary) ??
      undefined,
    dispensary_state: state as string | undefined,
    dutchie_pay_enabled:
      context.activeDispensary?.isDutchiePayEnabledForDispo ??
      contextCache.activeDispensary?.isDutchiePayEnabledForDispo,
  };
};

export const getUserContext = (context: AnalyticsContext, cache: ContextCache): UserContext => ({
  customer_id: context.activeUser?.id ?? cache.activeUser?.id,
  logged_in: context.activeUser?.isLoggedIn ?? cache.activeUser?.isLoggedIn,
  user_enrolled_dutchie_pay: context.activeUser?.isEnrolledDutchiePay ?? cache.activeUser?.isEnrolledDutchiePay,
});

export const getCartContext = (context: AnalyticsContext, cache: ContextCache): CartContext => ({
  checkout_token: context.activeCart?.checkoutToken ?? cache.activeCart?.checkoutToken,
});

export const getCheckoutToken = (context: AnalyticsContext, contextCache: ContextCache): string | undefined =>
  context.checkoutToken ?? context.activeOrder?.checkoutToken ?? contextCache.activeOrder?.checkoutToken;
